import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Tabs, Button, message, Switch, Space, Typography, Row, Col, Card, Tooltip, Select } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_URL } from '../../config';

const { Text } = Typography;

interface FloorEditProps {
  floorId: number;
  hotelId: number;
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  emHotelId?: number;
}

const LANGUAGES = [
  { code: 'en-US', label: 'English (US)' },
  { code: 'en-GB', label: 'English (UK)' },
  { code: 'nl-NL', label: 'Dutch' },
  { code: 'de-DE', label: 'German' },
  { code: 'es-ES', label: 'Spanish' },
  { code: 'fr-FR', label: 'French' },
];

const MEWS_LANGUAGES = [
  { code: 'en-US', label: 'English (US)' },
  { code: 'de-DE', label: 'German' },
  { code: 'fr-FR', label: 'French' },
  { code: 'it-IT', label: 'Italian' },
  { code: 'es-ES', label: 'Spanish' },
  { code: 'nl-NL', label: 'Dutch' },
];

const LANGUAGE_MATCH_MAP: Record<string, string[]> = {
  'en-US': ['english', 'en', 'en-us', 'en-gb'],
  'en-GB': ['english', 'en', 'en-us', 'en-gb'],
  'nl-NL': ['dutch', 'nl', 'nl-nl', 'nederlands'],
  'de-DE': ['german', 'de', 'de-de', 'deutsch'],
  'fr-FR': ['french', 'fr', 'fr-fr', 'français'],
  'es-ES': ['spanish', 'es', 'es-es', 'español'],
  'it-IT': ['italian', 'it', 'it-it', 'italiano'],
};

interface EmLanguage {
  id: number;
  code: string;
  label: string;
}

interface LanguageMapping {
  mewsLanguage: string;
  emLanguageId: number;
}

const FloorEdit: React.FC<FloorEditProps> = ({
  floorId,
  hotelId,
  visible,
  onClose,
  onSuccess,
  emHotelId,
}) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [floor, setFloor] = useState<any>(null);
  const [emLanguages, setEmLanguages] = useState<EmLanguage[]>([]);
  const [languageMappings, setLanguageMappings] = useState<LanguageMapping[]>([
    { mewsLanguage: '', emLanguageId: 0 },
  ]);
  const [pushModalVisible, setPushModalVisible] = useState(false);

  useEffect(() => {
    if (visible && floorId) {
      loadFloor();
    }
  }, [visible, floorId]);

  const loadFloor = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/floors/${floorId}`);
      const floorData = response.data;
      setFloor(floorData);

      form.setFieldsValue({
        floorNumber: floorData.floorNumber,
        isActive: floorData.isActive,
        ...LANGUAGES.reduce((acc, lang) => {
          acc[`name_${lang.code}`] = floorData.names?.[lang.code] || '';
          acc[`description_${lang.code}`] = floorData.descriptions?.[lang.code] || '';
          acc[`emName_${lang.code}`] = floorData.emNames?.[lang.code] || '';
          acc[`emDescription_${lang.code}`] = floorData.emDescriptions?.[lang.code] || '';
          return acc;
        }, {} as any),
      });
    } catch (error) {
      message.error('Failed to load floor');
    } finally {
      setLoading(false);
    }
  };

  const loadEmLanguages = async () => {
    try {
      if (emHotelId) {
        const response = await axios.get(`${API_URL}/em-hotels/${emHotelId}/languages`);
        const allLanguagesResponse = await axios.get(`${API_URL}/em-push/languages`);
        const allLanguages = allLanguagesResponse.data;
        const selectedCodes = response.data.map((lang: any) => lang.code);
        const filtered = allLanguages.filter((lang: EmLanguage) =>
          selectedCodes.includes(lang.code)
        );
        setEmLanguages(filtered);
        autoMatchMappings(filtered);
      } else {
        const response = await axios.get(`${API_URL}/em-push/languages`);
        setEmLanguages(response.data);
        autoMatchMappings(response.data);
      }
    } catch (error) {
      console.error('Failed to load EM languages');
    }
  };

  const autoMatchMappings = (langs: EmLanguage[]) => {
    const matched: LanguageMapping[] = [];
    for (const mewsLang of MEWS_LANGUAGES) {
      const possibleMatches = LANGUAGE_MATCH_MAP[mewsLang.code] || [mewsLang.code.split('-')[0], mewsLang.code];
      const emMatch = langs.find(emLang =>
        possibleMatches.some(m => emLang.code.toLowerCase() === m)
      );
      if (emMatch) {
        matched.push({
          mewsLanguage: mewsLang.code,
          emLanguageId: emMatch.id,
        });
      }
    }
    if (matched.length > 0) {
      setLanguageMappings(matched);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const names: Record<string, string> = {};
      const descriptions: Record<string, string> = {};
      const emNames: Record<string, string> = {};
      const emDescriptions: Record<string, string> = {};

      LANGUAGES.forEach((lang) => {
        if (values[`name_${lang.code}`]) {
          names[lang.code] = values[`name_${lang.code}`];
        }
        if (values[`description_${lang.code}`]) {
          descriptions[lang.code] = values[`description_${lang.code}`];
        }
        if (values[`emName_${lang.code}`]) {
          emNames[lang.code] = values[`emName_${lang.code}`];
        }
        if (values[`emDescription_${lang.code}`]) {
          emDescriptions[lang.code] = values[`emDescription_${lang.code}`];
        }
      });

      const payload = {
        names,
        descriptions,
        emNames,
        emDescriptions,
        floorNumber: values.floorNumber,
        isActive: values.isActive,
      };

      await axios.put(`${API_URL}/floors/${floorId}`, payload);

      message.success('Floor updated successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Save error:', error);
      message.error(error.response?.data?.message || 'Failed to update floor');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyToEm = (langCode: string) => {
    const values = form.getFieldsValue();
    const name = values[`name_${langCode}`];
    const description = values[`description_${langCode}`];

    if (!name && !description) {
      message.warning('No original content to copy');
      return;
    }

    form.setFieldsValue({
      [`emName_${langCode}`]: name || '',
      [`emDescription_${langCode}`]: description || '',
    });

    message.success('Original content copied to EM fields');
  };

  const handlePushToEm = async () => {
    const validMappings = languageMappings.filter(
      m => m.mewsLanguage && m.emLanguageId
    );

    if (validMappings.length === 0) {
      message.warning('Please add at least one complete language mapping');
      return;
    }

    // First save current form data
    try {
      const values = await form.validateFields();
      const emNames: Record<string, string> = {};
      const emDescriptions: Record<string, string> = {};

      LANGUAGES.forEach((lang) => {
        if (values[`emName_${lang.code}`]) {
          emNames[lang.code] = values[`emName_${lang.code}`];
        }
        if (values[`emDescription_${lang.code}`]) {
          emDescriptions[lang.code] = values[`emDescription_${lang.code}`];
        }
      });

      setPushing(true);

      await axios.post(`${API_URL}/floors/${floorId}/push-to-em`, {
        emHotelId,
        languageMappings: validMappings,
        emNames,
        emDescriptions,
      });

      message.success('Floor pushed to EM successfully');
      setPushModalVisible(false);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Push error:', error);
      message.error(error.response?.data?.message || 'Failed to push floor to EM');
    } finally {
      setPushing(false);
    }
  };

  const handleAddMapping = () => {
    setLanguageMappings([...languageMappings, { mewsLanguage: '', emLanguageId: 0 }]);
  };

  const handleRemoveMapping = (index: number) => {
    const newMappings = languageMappings.filter((_, i) => i !== index);
    setLanguageMappings(newMappings);
  };

  const getUsedMewsLanguages = () => {
    return languageMappings.filter(m => m.mewsLanguage).map(m => m.mewsLanguage);
  };

  const getUsedEmLanguages = () => {
    return languageMappings.filter(m => m.emLanguageId).map(m => m.emLanguageId);
  };

  const languageTabs = LANGUAGES.map((lang) => ({
    key: lang.code,
    label: lang.label,
    children: (
      <Row gutter={24} align="top">
        {/* Left side: Original Content */}
        <Col span={11}>
          <Card
            title="Original Content"
            size="small"
            styles={{
              header: { background: '#fafafa', fontWeight: 600 },
              body: { padding: 16 },
            }}
          >
            <Form.Item
              label="Name"
              name={`name_${lang.code}`}
            >
              <Input placeholder={`Floor name in ${lang.label}`} />
            </Form.Item>

            <Form.Item
              label="Description"
              name={`description_${lang.code}`}
            >
              <Input.TextArea
                placeholder={`Description in ${lang.label}`}
                rows={4}
              />
            </Form.Item>
          </Card>
        </Col>

        {/* Middle: Copy button */}
        <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 60 }}>
          <Tooltip title="Copy original content to EM fields">
            <Button
              type="primary"
              shape="circle"
              icon={<ArrowRightOutlined />}
              size="large"
              onClick={() => handleCopyToEm(lang.code)}
              style={{ boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)' }}
            />
          </Tooltip>
        </Col>

        {/* Right side: EM Content */}
        <Col span={11}>
          <Card
            title="EM Content"
            size="small"
            styles={{
              header: { background: '#f0f9ff', fontWeight: 600, color: '#1890ff' },
              body: { padding: 16 },
            }}
          >
            <Form.Item
              label="EM Name"
              name={`emName_${lang.code}`}
            >
              <Input placeholder={`EM name in ${lang.label}`} />
            </Form.Item>

            <Form.Item
              label="EM Description"
              name={`emDescription_${lang.code}`}
            >
              <Input.TextArea
                placeholder={`EM description in ${lang.label}`}
                rows={4}
              />
            </Form.Item>
          </Card>
        </Col>
      </Row>
    ),
  }));

  return (
    <>
      {contextHolder}
      <Modal
        title={`Edit Floor: ${floor?.floorNumber || ''}`}
        open={visible}
        onCancel={onClose}
        footer={[
          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>,
          <Button
            key="push"
            onClick={() => {
              if (floor?.emFloorId) {
                // Allow re-push to update translations
              }
              loadEmLanguages();
              setPushModalVisible(true);
            }}
            disabled={!emHotelId}
          >
            {floor?.emFloorId ? 'Re-Push to EM' : 'Push to EM'}
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={saving}
            onClick={handleSave}
          >
            Save
          </Button>,
        ]}
        width={1200}
        destroyOnClose
        style={{ top: 20 }}
        styles={{
          body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' },
        }}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
            <Text strong>Floor Number:</Text>
            <Form.Item
              name="floorNumber"
              style={{ marginBottom: 0 }}
            >
              <Input style={{ width: 100 }} />
            </Form.Item>
            <div style={{ marginLeft: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text strong>Active:</Text>
              <Form.Item
                name="isActive"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Switch />
              </Form.Item>
            </div>
            {floor?.roomCount !== undefined && (
              <div style={{ marginLeft: 24 }}>
                <Text strong>Rooms: </Text>
                <Text>{floor.roomCount}</Text>
              </div>
            )}
            {floor?.emFloorId && (
              <div style={{ marginLeft: 24 }}>
                <Text type="success">Pushed to EM (ID: {floor.emFloorId})</Text>
              </div>
            )}
          </div>

          <Tabs items={languageTabs} />
        </Form>
      </Modal>

      {/* Push to EM Modal */}
      <Modal
        title="Push Floor to EM"
        open={pushModalVisible}
        onCancel={() => setPushModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setPushModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="push"
            type="primary"
            loading={pushing}
            onClick={handlePushToEm}
          >
            Push to EM
          </Button>,
        ]}
        width={600}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text type="secondary">
            Map languages to push floor translations to EM. Make sure you have saved EM content before pushing.
          </Text>

          {languageMappings.map((mapping, index) => (
            <Space key={index} align="start" style={{ width: '100%' }}>
              <Select
                style={{ width: 200 }}
                placeholder="Select Source Language"
                value={mapping.mewsLanguage || undefined}
                onChange={(value) => {
                  const newMappings = [...languageMappings];
                  newMappings[index].mewsLanguage = value;
                  setLanguageMappings(newMappings);
                }}
              >
                {MEWS_LANGUAGES.map((lang) => (
                  <Select.Option
                    key={lang.code}
                    value={lang.code}
                    disabled={getUsedMewsLanguages().includes(lang.code) && mapping.mewsLanguage !== lang.code}
                  >
                    {lang.label} ({lang.code})
                  </Select.Option>
                ))}
              </Select>

              <Text style={{ padding: '4px 8px' }}>→</Text>

              <Select
                style={{ width: 200 }}
                placeholder="Select EM Language"
                value={mapping.emLanguageId || undefined}
                onChange={(value) => {
                  const newMappings = [...languageMappings];
                  newMappings[index].emLanguageId = value;
                  setLanguageMappings(newMappings);
                }}
              >
                {emLanguages.map((lang) => (
                  <Select.Option
                    key={lang.id}
                    value={lang.id}
                    disabled={getUsedEmLanguages().includes(lang.id) && mapping.emLanguageId !== lang.id}
                  >
                    {lang.label || lang.code}
                  </Select.Option>
                ))}
              </Select>

              {languageMappings.length > 1 && (
                <Button
                  type="text"
                  danger
                  onClick={() => handleRemoveMapping(index)}
                >
                  Remove
                </Button>
              )}
            </Space>
          ))}

          <Button
            type="dashed"
            onClick={handleAddMapping}
            style={{ width: '100%' }}
          >
            + Add Language Mapping
          </Button>
        </Space>
      </Modal>
    </>
  );
};

export default FloorEdit;
